import { z } from "zod"
import { stopSchema } from "./base.grpahql"
import { tripSchema } from "./planner.graphql"

export function graphqlResponse<TData extends z.ZodRawShape>(
  data: z.ZodObject<TData>
) {
  return z.object({
    data: data.optional(),
    error: z.unknown(),
  })
}

const planConnectionLegSchema = z.object({
  from: z.object({
    lat: z.number(),
    lon: z.number(),
    name: z.string(),
  }),
  to: z.object({
    lat: z.number(),
    lon: z.number(),
    name: z.string(),
  }),
  startTime: z.number().nullish(),
  endTime: z.number().nullish(),
  legGeometry: z.object({ points: z.string() }),
  mode: z.string(),
  arrivalDelay: z.number(),
  duration: z.number(),
  distance: z.number(),
  generalizedCost: z.number(),
  agency: z.object({
    gtfsId: z.string().nullish(),
    id: z.string().nullish(),
    name: z.string().nullish(),
    url: z.string().nullish(),
    phone: z.string().nullish(),
    fareUrl: z.string().nullish(),
    alerts: z.array(z.unknown()).nullish(),
  }).nullish(),
  trip: tripSchema.nullish(),
  route: z.object({
    gtfsId: z.string(),
    longName: z.string(),
  }).nullish(),
  intermediateStops: z.array(stopSchema).optional().nullable(),
  dropoffType: z.string().nullish(),
  pickupType: z.string().nullish(),
  pickupBookingInfo: z.object({
    message: z.string().nullish(),
    pickupMessage: z.string().nullish(),
    dropOffMessage: z.unknown().nullish(),
    minimumBookingNoticeSeconds: z.unknown().nullish(),
    maximumBookingNoticeSeconds: z.unknown().nullish(),
    contactInfo: z.object({
      contactPerson: z.unknown().nullish(),
      phoneNumber: z.string().nullish(),
      eMail: z.unknown().nullish(),
      faxNumber: z.unknown().nullish(),
      infoUrl: z.string().nullish(),
      bookingUrl: z.unknown().nullish(),
      additionalDetails: z.unknown().nullish(),
    }),
    latestBookingTime: z.object({
      time: z.string(),
      daysPrior: z.number(),
    }).nullish(),
    earliestBookingTime: z.object({
      time: z.string(),
      daysPrior: z.number(),
    }).nullish(),
  }).nullish(),
  dropOffBookingInfo: z.object({
    message: z.string().nullish(),
    pickupMessage: z.string().nullish(),
    dropOffMessage: z.unknown().nullish(),
    minimumBookingNoticeSeconds: z.unknown().nullish(),
    maximumBookingNoticeSeconds: z.unknown().nullish(),
    contactInfo: z.object({
      contactPerson: z.unknown().nullish(),
      phoneNumber: z.string().nullish(),
      eMail: z.unknown().nullish(),
      faxNumber: z.unknown().nullish(),
      infoUrl: z.string().nullish(),
      bookingUrl: z.unknown().nullish(),
      additionalDetails: z.unknown().nullish(),
    }),
    latestBookingTime: z.object({
      time: z.string(),
      daysPrior: z.number(),
    }).nullish(),
    earliestBookingTime: z.object({
      time: z.string(),
      daysPrior: z.number(),
    }).nullish(),
  }).nullish(),
  steps: z.array(
    z.object({
      distance: z.number(),
      bogusName: z.boolean(),
      streetName: z.string(),
      stayOn: z.boolean(),
      relativeDirection: z.string(),
      absoluteDirection: z.string(),
      lat: z.number(),
      lon: z.number(),
    })
  ).nullish(),
})

export const planConnectionGraphqlResponse = graphqlResponse(
  z.object({
    planConnection: z.object({
      edges: z.array(
        z.object({
          node: z.object({
            duration: z.number().nullish(),
            generalizedCost: z.number().nullish(),
            waitingTime: z.number().nullish(),
            startTime: z.number().nullish(),
            endTime: z.number().nullish(),
            legs: z.array(planConnectionLegSchema).nullish(),
          }),
        })
      ).nullish(),
      routingErrors: z.array(
        z.object({
          code: z.string().nullish(),
          inputField: z.string().nullish(),
          description: z.string().nullish(),
        })
      ).nullish(),
    }).nullish(),
  })
)

export type PlanConnectionGraphqlResponse = z.infer<
  typeof planConnectionGraphqlResponse
>
export type PlanConnectionGraphqlData = NonNullable<
  PlanConnectionGraphqlResponse["data"]
>
export type PlanConnection = NonNullable<
  PlanConnectionGraphqlData["planConnection"]
>
export type PlanConnectionEdges = NonNullable<PlanConnection["edges"]>
export type PlanConnectionEdge = PlanConnectionEdges[number]
export type PlanConnectionNode = PlanConnectionEdge["node"]
export type PlanConnectionLegs = PlanConnectionNode["legs"]
export type PlanConnectionLeg = z.infer<typeof planConnectionLegSchema>
export type PlanConnectionSteps = NonNullable<PlanConnectionLeg["steps"]>
export type PlanConnectionStep = PlanConnectionSteps[number]

type GqlOperation = "query" | "mutation" | "subscription "
export type QueryName<T> =
  T extends `${string}${GqlOperation} ${infer TName}${"("}${infer TRest}`
    ? TName | QueryName<TRest>
    : T extends `${string}${GqlOperation} ${infer TName}${" {"}${infer TRest}`
      ? TName | QueryName<TRest>
      : never
